/**
 * UI ephemeral store — état NON persisté.
 *
 * @remarks
 * Tout ce qui est transitoire et ne doit PAS survivre à un refresh :
 * - drag in flight (tab en cours de déplacement)
 * - hover state
 * - draft d'input non encore committé
 *
 * **Pourquoi un store séparé** : éviter le piège du `partialize` dans
 * `use-layout-store.ts` qui filtre les fields à exclure de la persist —
 * un dev qui ajoute un field oublie de le filtrer → field transitoire
 * persisté en `localStorage` → bugs au reload.
 *
 * Avec un store dédié, le contrat est clair : **tout ce qui est dans
 * ce store est éphémère par construction, aucune persist possible**.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface DraggingTab {
  readonly tabId: string;
  readonly sourcePanel: 'left' | 'right';
  readonly ghostX: number;
  readonly ghostY: number;
}

interface UIEphemeralState {
  draggingTab: DraggingTab | null;
  setDraggingTab: (tab: DraggingTab | null) => void;
  updateDragGhost: (x: number, y: number) => void;
}

export const useUIEphemeralStore = create<UIEphemeralState>()(
  devtools(
    (set) => ({
      draggingTab: null,
      setDraggingTab: (tab) => {
        set({ draggingTab: tab });
      },
      updateDragGhost: (x, y) => {
        set((s) =>
          s.draggingTab ? { draggingTab: { ...s.draggingTab, ghostX: x, ghostY: y } } : s,
        );
      },
    }),
    { name: 'ui-ephemeral', enabled: import.meta.env.DEV },
  ),
);
